import { useCallback } from 'react';
import every from 'lodash/every';
import filter from 'lodash/filter';
import size from 'lodash/size';

import {
  MaterialsSetSelectedMaterialsID,
  UpdateMaterialsSetCacheAction
} from '../../../../../../../../materialsSets/materialsSetsTypes';

import {
  SubmitSelectedMaterialsToProjectQueryResponse,
  SUBMIT_SELECTED_MATERIALS_TO_PROJECT_QUERY
} from '../../../../../../../../projects/queries/submitSelectedMaterialsToProject.query';

import { useSubmitSelectedMaterialsToProject } from '../../../../../../../../projects/hooks/useSubmitSelectedMaterialsToProject';

import {
  SubmitSelectedMaterialsToTaskQueryResponse,
  SUBMIT_SELECTED_MATERIALS_TO_TASK_QUERY
} from '../../../../../../../../tasks/queries/submitSelectedMaterialsToTask.query';

import { useSubmitSelectedMaterialsToTask } from '../../../../../../../../tasks/hooks/useSubmitSelectedMaterialsToTask';

import { SelectProjectAndTaskModalButtonOnSubmitAction } from '../../../../../../../../common/components/modalButtons/SelectProjectAndTaskModalButton';

import { MaterialsSetCache } from '../../../../../../../../materialsSets/MaterialsSetCache';

import { SelectedMaterialsSidebarAttachModalButtonMaterialsSet } from '../../SelectedMaterialsSidebarAttachModalButton.types';

interface UseSelectedMaterialsSidebarAttachModalButtonOptions {
  selectedMaterialIds: MaterialsSetSelectedMaterialsID[];
  onClose: () => void;
  updateMaterialsSetCache: UpdateMaterialsSetCacheAction<SelectedMaterialsSidebarAttachModalButtonMaterialsSet>;
}

function useSelectedMaterialsSidebarAttachModalButton({
  selectedMaterialIds,
  onClose,
  updateMaterialsSetCache
}: UseSelectedMaterialsSidebarAttachModalButtonOptions) {
  const {
    submitSelectedMaterialsToProjectErrorMessage,
    submitSelectedMaterialsToProjectLoading,
    submitSelectedMaterialsToProject,
    submitSelectedMaterialsToProjectReset
  } = useSubmitSelectedMaterialsToProject<SubmitSelectedMaterialsToProjectQueryResponse>(
    {
      query: SUBMIT_SELECTED_MATERIALS_TO_PROJECT_QUERY,
      cacheKeys: [MaterialsSetCache.showCacheKey()]
    }
  );

  const {
    submitSelectedMaterialsToTaskErrorMessage,
    submitSelectedMaterialsToTaskLoading,
    submitSelectedMaterialsToTask,
    submitSelectedMaterialsToTaskReset
  } = useSubmitSelectedMaterialsToTask<SubmitSelectedMaterialsToTaskQueryResponse>(
    {
      query: SUBMIT_SELECTED_MATERIALS_TO_TASK_QUERY,
      cacheKeys: [MaterialsSetCache.showCacheKey()]
    }
  );

  const handleAfterSubmit = useCallback<() => void>(() => {
    updateMaterialsSetCache({
      updateFunction: (prevMaterialSet) => ({
        ...prevMaterialSet,
        selectedMaterials:
          size(selectedMaterialIds) > 0
            ? filter(prevMaterialSet.selectedMaterials, (selectedMaterial) =>
                every(selectedMaterialIds, (id) => id !== selectedMaterial.id)
              )
            : []
      })
    });
    onClose();
  }, [onClose, selectedMaterialIds, updateMaterialsSetCache]);

  const handleSubmit =
    useCallback<SelectProjectAndTaskModalButtonOnSubmitAction>(
      ({ projectUuid, taskUuid }) => {
        if (taskUuid) {
          return submitSelectedMaterialsToTask({
            uuid: taskUuid,
            selectedMaterialIds
          }).then(() => {
            handleAfterSubmit();
          });
        }
        return submitSelectedMaterialsToProject({
          uuid: projectUuid,
          selectedMaterialIds
        }).then(() => {
          handleAfterSubmit();
        });
      },
      [
        submitSelectedMaterialsToProject,
        selectedMaterialIds,
        submitSelectedMaterialsToTask,
        handleAfterSubmit
      ]
    );

  const handleClose = useCallback<() => void>(() => {
    submitSelectedMaterialsToProjectReset();
    submitSelectedMaterialsToTaskReset();
  }, [
    submitSelectedMaterialsToProjectReset,
    submitSelectedMaterialsToTaskReset
  ]);

  return {
    isLoading:
      submitSelectedMaterialsToProjectLoading ||
      submitSelectedMaterialsToTaskLoading,
    errorMessage:
      submitSelectedMaterialsToProjectErrorMessage ||
      submitSelectedMaterialsToTaskErrorMessage,
    handleSubmit,
    handleClose
  };
}

export default useSelectedMaterialsSidebarAttachModalButton;
